import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRefuseTask,
  useDelegateTask,
  useUpdateTaskContext,
  getGetTodayQueryKey,
  getListProjectsQueryKey,
  getGetProjectQueryKey,
  getListTasksQueryKey,
  type Task,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserX, UserCheck, FileText, Search, MoreHorizontal, Mail, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface ContactRow {
  id: string;
  name: string;
  email: string;
  company?: string | null;
}

function useContacts() {
  return useQuery<ContactRow[]>({
    queryKey: ["crm-contacts-mini"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/crm/contacts`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return (await res.json()) as ContactRow[];
    },
    staleTime: 60_000,
  });
}

function invalidateTaskRelated(
  qc: ReturnType<typeof useQueryClient>,
  task: Task,
) {
  qc.invalidateQueries({ queryKey: getGetTodayQueryKey() });
  qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
  qc.invalidateQueries({ queryKey: getListTasksQueryKey(), exact: false });
  if (task.projectId) {
    qc.invalidateQueries({ queryKey: getGetProjectQueryKey(task.projectId) });
  }
}

function ContextDialog({
  task,
  open,
  onClose,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [text, setText] = useState(task.description ?? "");
  const updateCtx = useUpdateTaskContext();

  function handleSave() {
    updateCtx.mutate(
      { id: task.id, data: { description: text } },
      {
        onSuccess: () => {
          toast({ title: "Contexte enregistré" });
          invalidateTaskRelated(qc, task);
          onClose();
        },
        onError: () => toast({ title: "Erreur", variant: "destructive" }),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) setText(task.description ?? "");
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-sm p-4">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Note de contexte</DialogTitle>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Contexte, lien, décision rapide…"
          className="resize-none text-sm"
          autoFocus
        />
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={updateCtx.isPending}
          >
            Annuler
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={updateCtx.isPending}
            data-testid="button-context-save"
          >
            {updateCtx.isPending && (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            )}
            Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DelegateDialog({
  task,
  open,
  onClose,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(
    task.assignedToContactId ?? null,
  );
  const [selectedName, setSelectedName] = useState(task.assignedToName ?? "");
  const [note, setNote] = useState(task.delegationNote ?? "");
  const delegateMut = useDelegateTask();
  const { data: contacts = [] } = useContacts();

  const filtered = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()),
  );

  function handleOpen(v: boolean) {
    if (v) {
      setSearch("");
      setSelectedId(task.assignedToContactId ?? null);
      setSelectedName(task.assignedToName ?? "");
      setNote(task.delegationNote ?? "");
    }
    if (!v) onClose();
  }

  function selectContact(c: ContactRow) {
    setSelectedId(c.id);
    setSelectedName(c.name);
    setSearch("");
  }

  function handleDelegate() {
    const name = selectedName.trim() || search.trim();
    if (!name && !selectedId) {
      toast({ title: "Précisez un contact ou un nom", variant: "destructive" });
      return;
    }
    delegateMut.mutate(
      {
        id: task.id,
        data: {
          assignedToContactId: selectedId ?? null,
          assignedToName: name || null,
          delegationNote: note.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast({ title: "Tâche déléguée" });
          invalidateTaskRelated(qc, task);
          onClose();
        },
        onError: () =>
          toast({ title: "Erreur lors de la délégation", variant: "destructive" }),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-sm p-4">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Déléguer à…</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedId(null);
              setSelectedName(e.target.value);
            }}
            placeholder="Nom ou email du contact…"
            className="pl-7 h-8 text-sm"
            autoFocus
          />
        </div>
        {search.length > 0 && filtered.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden max-h-36 overflow-y-auto">
            {filtered.slice(0, 6).map((c) => (
              <button
                key={c.id}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors ${
                  selectedId === c.id ? "bg-primary/10 text-primary font-medium" : ""
                }`}
                onClick={() => selectContact(c)}
              >
                <span className="font-medium">{c.name}</span>
                {c.email && (
                  <span className="text-muted-foreground ml-2 text-xs">{c.email}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {selectedId && (
          <p className="text-xs text-primary">✓ {selectedName}</p>
        )}
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Note de délégation (optionnelle)…"
          className="resize-none text-sm"
        />
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={delegateMut.isPending}
          >
            Annuler
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleDelegate}
            disabled={delegateMut.isPending}
            data-testid="button-delegate-confirm"
          >
            {delegateMut.isPending && (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            )}
            Déléguer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RefuseDialog({
  task,
  open,
  onClose,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [reason, setReason] = useState("");
  const refuseMut = useRefuseTask();

  function handleRefuse() {
    refuseMut.mutate(
      { id: task.id, data: { reason: reason.trim() || undefined } },
      {
        onSuccess: () => {
          toast({ title: "Tâche refusée" });
          invalidateTaskRelated(qc, task);
          onClose();
        },
        onError: () => toast({ title: "Erreur", variant: "destructive" }),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) setReason("");
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-sm p-4">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Refuser cette tâche ?</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          Elle sera archivée. Ajoutez une raison courte (optionnel).
        </p>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Raison du refus…"
          className="resize-none text-sm"
          autoFocus
        />
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={refuseMut.isPending}
          >
            Annuler
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={handleRefuse}
            disabled={refuseMut.isPending}
            data-testid="button-refuse-confirm"
          >
            {refuseMut.isPending && (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            )}
            Refuser
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TaskSourceData {
  description: string | null;
  email: {
    subject: string | null;
    fromName: string | null;
    fromAddress: string | null;
    summary: string | null;
    bodySnippet: string | null;
    receivedAt: string | null;
  } | null;
}

function useTaskSource(taskId: string, enabled: boolean) {
  return useQuery<TaskSourceData>({
    queryKey: ["task-source", taskId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/tasks/${taskId}/source`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Erreur lors du chargement du contexte");
      return (await res.json()) as TaskSourceData;
    },
    enabled,
    staleTime: 30_000,
  });
}

function SourceDialog({
  task,
  open,
  onClose,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useTaskSource(task.id, open);

  const hasEmail = !!data?.email;
  const hasDescription = !!data?.description;
  const hasAnything = hasEmail || hasDescription;

  function formatSender(src: TaskSourceData["email"]) {
    if (!src) return null;
    if (src.fromName && src.fromAddress) return `${src.fromName} <${src.fromAddress}>`;
    return src.fromName ?? src.fromAddress ?? null;
  }

  function formatDate(iso: string | null) {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(iso));
    } catch {
      return null;
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md p-4 gap-3">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            Contexte de la tâche
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm font-medium text-foreground -mt-1 line-clamp-3">{task.title}</p>

        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <p className="text-xs text-destructive">Impossible de charger le contexte.</p>
        )}

        {data && !hasAnything && (
          <p className="text-xs text-muted-foreground italic">
            Aucun contexte ni email source associé à cette tâche.
          </p>
        )}

        {hasDescription && (
          <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-1">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Note de contexte</p>
            <p className="text-sm text-foreground whitespace-pre-wrap">{data!.description}</p>
          </div>
        )}

        {hasEmail && (
          <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2">
            <div className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Email source</p>
              {data!.email!.receivedAt && (
                <span className="ml-auto text-[10px] text-muted-foreground">{formatDate(data!.email!.receivedAt)}</span>
              )}
            </div>
            {data!.email!.subject && (
              <p className="text-xs font-semibold text-foreground">{data!.email!.subject}</p>
            )}
            {formatSender(data!.email) && (
              <p className="text-xs text-muted-foreground">De : {formatSender(data!.email)}</p>
            )}
            {data!.email!.summary && (
              <div className="border-l-2 border-primary/30 pl-2">
                <p className="text-xs text-foreground">{data!.email!.summary}</p>
              </div>
            )}
            {data!.email!.bodySnippet && (
              <details className="group">
                <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground select-none">
                  Voir le corps du mail…
                </summary>
                <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {data!.email!.bodySnippet}
                </p>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DelegationBadge({ task }: { task: Task }) {
  if (task.status !== "delegated") return null;
  const name = task.assignedToName ?? "quelqu'un";
  const first = name.split(" ")[0];
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full"
      title={task.delegationNote ?? undefined}
      data-testid="badge-delegated"
    >
      ⏳ {first}
    </span>
  );
}

export default function TaskQuickActions({ task }: { task: Task }) {
  const [active, setActive] = useState<"source" | "context" | "delegate" | "refuse" | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-8 px-0 text-muted-foreground hover:text-foreground"
            data-testid="button-task-more"
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => setTimeout(() => setActive("source"), 0)}>
            <Info className="w-3.5 h-3.5 mr-2" />
            Voir le contexte
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTimeout(() => setActive("context"), 0)}>
            <FileText className="w-3.5 h-3.5 mr-2" />
            + Note de contexte
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTimeout(() => setActive("delegate"), 0)}>
            <UserCheck className="w-3.5 h-3.5 mr-2" />
            Déléguer à…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setTimeout(() => setActive("refuse"), 0)}
            className="text-destructive focus:text-destructive"
          >
            <UserX className="w-3.5 h-3.5 mr-2" />
            Refuser
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SourceDialog
        task={task}
        open={active === "source"}
        onClose={() => setActive(null)}
      />
      <ContextDialog
        task={task}
        open={active === "context"}
        onClose={() => setActive(null)}
      />
      <DelegateDialog
        task={task}
        open={active === "delegate"}
        onClose={() => setActive(null)}
      />
      <RefuseDialog
        task={task}
        open={active === "refuse"}
        onClose={() => setActive(null)}
      />
    </>
  );
}
